import React, {
  useCallback, useEffect, useRef, useState,
} from "react"
import { Box, SystemStyleObject } from "@chakra-ui/react"
import InfiniteViewer, { OnPinch, OnScroll } from "react-infinite-viewer"
import Selecto, { OnDragStart, OnSelect, OnSelectEnd } from "react-selecto"
import { Point, Rect, Size } from "./lib/viewport"
import { rectBetween } from "./lib/selection"
import { isControlTarget, isTypingTarget } from "./lib/keybindings"
import { ViewportState } from "./stores/createViewportStore"
import {
  useContentScale, useGetViewport, useViewport, useViewportStore,
} from "./Editor"
import { pressMissesObject } from "./ObjectLayer"
import { repeatedDistanceStyles } from "./useOneDistancePerGap"

/** The canvas's look, and the overrides for the libraries drawn on it. */
const canvasStyles: SystemStyleObject = {
  "& > .editor-viewer": { position: "absolute", inset: 0 },
  "& .moveable-control-box": { "--moveable-color": "#3182ce" },
  // Moveable scales the snap distances by its zoom twice; once keeps their screen size
  "& .moveable-control-box .moveable-guideline-group .moveable-size-value": {
    fontSize: "12px",
    fontWeight: "medium",
    color: "orange.500",
  },
  "& .moveable-control-box .moveable-line.moveable-guideline.moveable-gap": { background: "orange.500" },
  "& .moveable-control-box .moveable-line.moveable-vertical.moveable-bold": { width: "1px" },
  "& .moveable-control-box .moveable-line.moveable-horizontal.moveable-bold": { height: "1px" },
  ...repeatedDistanceStyles,
  "& .editor-selection-box": {
    border: "1px solid #4299E1",
    background: "rgba(66, 153, 225, 0.08)",
  },
}

// Space pans over everything on the canvas, so the hand shows over it all
const panningStyles: SystemStyleObject = { "& *": { cursor: "inherit !important" } }

export type SelectionBox = {
  /** The objects it touches, by `data-object-id`. */
  ids: string[]
  /** Its rectangle in content units. */
  rect: Rect
}

type Props = {
  /** The content's size, in content units. */
  contentSize: Size
  /** Room kept around the content when panning, in content units. */
  padding: number
  /** The view resets to 100%, centred, when this or the content size changes. */
  resetKey?: string
  /** A click on the background. */
  onBackgroundClick?: () => void
  /**
   * A selection box dragged from the background, on every move so the selection can follow
   * it live, then `null` when it's released. `additive` with Shift. Objects are the elements
   * marked `data-object-id`; anything else counts as background.
   */
  onSelectionBoxChange?: (box: SelectionBox | null, additive: boolean) => void
  /** Any mouse-down on the canvas, before it pans or selects. */
  onMouseDown?: (event: React.MouseEvent) => void
  children: React.ReactNode
}

// Anywhere but an object or the selection's handles, or inside an unfilled shape
const isBackground = (event: MouseEvent) => (
  !(event.target as Element).closest?.("[data-object-id], .moveable-control-box")
  || pressMissesObject(event)
)

const WHEEL_ZOOM_SENSITIVITY = 0.004
const SAME = 0.01

/**
 * The editor's canvas: pans with the wheel, the middle button or Space-drag, zooms with ⌘ +
 * wheel or a pinch, draws its children in content coordinates and selects with a box dragged
 * from the background. The viewport store leads: the view follows its zoom and pan.
 */
const EditorCanvas = ({
  contentSize,
  padding,
  resetKey,
  onBackgroundClick,
  onSelectionBoxChange,
  onMouseDown,
  children,
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [container, setContainerElement] = useState<HTMLDivElement | null>(null)
  const attachContainer = useCallback((element: HTMLDivElement | null) => {
    containerRef.current = element
    setContainerElement(element)
  }, [])
  const viewerRef = useRef<InfiniteViewer>(null)
  const viewportStore = useViewportStore()
  const scale = useContentScale()
  const getViewport = useGetViewport()
  const [isPanning, setIsPanning] = useState(false)
  const spaceHeld = useViewport((state) => state.spaceHeld)
  const lastPanPoint = useRef({ x: 0, y: 0 })
  const onSelectionBoxChangeRef = useRef(onSelectionBoxChange)
  onSelectionBoxChangeRef.current = onSelectionBoxChange

  useEffect(() => {
    getViewport().setContainer(containerRef.current)

    return () => getViewport().setContainer(null)
  }, [getViewport])

  // The view follows the store; while it's being moved to match, its own events are ignored
  const following = useRef(false)
  const contentRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const follow = ({ zoom, pan }: ViewportState) => {
      contentRef.current?.style.setProperty("--editor-zoom", String(zoom))
      const viewer = viewerRef.current
      if (!viewer) return

      const left = -pan.x / zoom
      const top = -pan.y / zoom
      if (
        viewer.getZoom() === zoom
        && Math.abs(viewer.getScrollLeft() - left) < SAME
        && Math.abs(viewer.getScrollTop() - top) < SAME
      ) return

      following.current = true
      viewer.setZoom(zoom)
      viewer.scrollTo(left, top)
      following.current = false
    }

    follow(viewportStore.getState())

    return viewportStore.subscribe(follow)
  }, [viewportStore])

  const handleScroll = useCallback(({ scrollLeft, scrollTop }: OnScroll) => {
    if (following.current) return

    const { zoom, setView } = getViewport()
    setView(zoom, { x: -scrollLeft * zoom, y: -scrollTop * zoom })
  }, [getViewport])

  const handlePinch = useCallback(({ zoom, clientX, clientY }: OnPinch) => {
    const rect = containerRef.current!.getBoundingClientRect()
    const { zoomOptions: { min, max }, zoomTo } = getViewport()
    zoomTo(Math.max(min, Math.min(max, zoom)), { x: clientX - rect.left, y: clientY - rect.top })
  }, [getViewport])

  // Holding Space turns a left-drag into panning, anywhere on the canvas
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // On a button, a select or a field, Space is theirs
      if (event.code !== "Space" || isControlTarget(event.target)) return
      event.preventDefault()
      if (!event.repeat) getViewport().setSpaceHeld(true)
    }
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === "Space") getViewport().setSpaceHeld(false)
    }
    const onBlur = () => getViewport().setSpaceHeld(false)

    document.addEventListener("keydown", onKeyDown)
    document.addEventListener("keyup", onKeyUp)
    window.addEventListener("blur", onBlur)

    return () => {
      document.removeEventListener("keydown", onKeyDown)
      document.removeEventListener("keyup", onKeyUp)
      window.removeEventListener("blur", onBlur)
    }
  }, [getViewport])

  useEffect(() => {
    getViewport().setContent(contentSize, padding)
    getViewport().resetZoom()
  }, [getViewport, resetKey, contentSize.width, contentSize.height, padding])

  // The selection box, from Selecto: the objects come from it, the rectangle from the pointer
  const box = useRef<{ start: Point; additive: boolean; ids: string[] } | null>(null)

  const contentBox = (start: Point, end: Point) => {
    const { screenToContent } = getViewport()
    const from = screenToContent(start)
    const to = screenToContent(end)

    return from && to ? rectBetween(from, to) : null
  }

  const reportBox = (end: Point) => {
    const { current } = box
    const rect = current && contentBox(current.start, end)
    if (!current || !rect) return

    onSelectionBoxChangeRef.current?.({ ids: current.ids, rect }, current.additive)
  }

  // Asked before Selecto measures the objects, so a press on one costs nothing
  const mayStartBox = ({ inputEvent }: { inputEvent: MouseEvent }) => (
    !getViewport().spaceHeld && inputEvent.button === 0 && isBackground(inputEvent)
  )

  const handleBoxStart = (event: OnDragStart) => {
    const input = event.inputEvent as MouseEvent
    box.current = {
      start: { x: input.clientX, y: input.clientY }, additive: input.shiftKey, ids: [],
    }
  }

  const handleBoxSelect = (event: OnSelect) => {
    if (!box.current) return

    box.current.ids = event.selected
      .map((element) => (element as HTMLElement).dataset.objectId)
      .filter((id): id is string => !!id)
    reportBox({ x: event.inputEvent.clientX, y: event.inputEvent.clientY })
  }

  const handleBoxEnd = (event: OnSelectEnd) => {
    const { current } = box
    box.current = null
    if (!current) return

    // A Shift-click on the background keeps the selection, like Figma
    if (event.isClick) {
      if (!current.additive) onBackgroundClick?.()
    } else {
      onSelectionBoxChangeRef.current?.(null, current.additive)
    }
  }

  // With Space held, a left press pans, before any object can take it
  const handleMouseDownCapture = useCallback((event: React.MouseEvent) => {
    if (!spaceHeld || event.button !== 0) return

    event.preventDefault()
    event.stopPropagation()
    setIsPanning(true)
    lastPanPoint.current = { x: event.clientX, y: event.clientY }
  }, [spaceHeld])

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    onMouseDown?.(event)

    if (event.button === 1) {
      event.preventDefault()
      setIsPanning(true)
      lastPanPoint.current = { x: event.clientX, y: event.clientY }
    } else if (event.button === 0 && isBackground(event.nativeEvent)) {
      // Keeps text from being selected, which also keeps the focus: a field gives it up here
      event.preventDefault()
      if (isTypingTarget(document.activeElement)) (document.activeElement as HTMLElement).blur()
    }
  }, [onMouseDown])

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!isPanning) return

    getViewport().panBy({
      x: event.clientX - lastPanPoint.current.x,
      y: event.clientY - lastPanPoint.current.y,
    })
    lastPanPoint.current = { x: event.clientX, y: event.clientY }
  }, [isPanning, getViewport])

  const handleMouseUp = useCallback(() => setIsPanning(false), [])

  return (
    <Box
      ref={attachContainer}
      position="absolute"
      inset={0}
      overflow="hidden"
      bg="white"
      backgroundImage="radial-gradient(circle, #9ca3af 1px, transparent 1px)"
      backgroundSize="20px 20px"
      style={{ backgroundAttachment: "fixed" }}
      cursor={isPanning ? "grabbing" : spaceHeld ? "grab" : "default"}
      onMouseDownCapture={handleMouseDownCapture}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      sx={spaceHeld || isPanning ? { ...canvasStyles, ...panningStyles } : canvasStyles}
    >
      <InfiniteViewer
        ref={viewerRef}
        className="editor-viewer"
        useWheelScroll
        useWheelPinch
        wheelPinchKey="meta"
        wheelScale={WHEEL_ZOOM_SENSITIVITY}
        usePinch
        useMouseDrag={false}
        useAutoZoom={false}
        useResizeObserver
        displayHorizontalScroll={false}
        displayVerticalScroll={false}
        onScroll={handleScroll}
        onPinch={handlePinch}
      >
        <div
          ref={contentRef}
          style={{
            width: contentSize.width * scale, height: contentSize.height * scale, userSelect: "none",
          }}
        >
          {children}
        </div>
      </InfiniteViewer>
      {onSelectionBoxChange && container && (
        <Selecto
          className="editor-selection-box"
          container={container}
          dragContainer={container}
          selectableTargets={["[data-object-id]"]}
          selectByClick={false}
          selectFromInside
          hitRate={0}
          dragCondition={mayStartBox}
          onDragStart={handleBoxStart}
          onSelect={handleBoxSelect}
          onDrag={(event) => reportBox({ x: event.clientX, y: event.clientY })}
          onSelectEnd={handleBoxEnd}
        />
      )}
    </Box>
  )
}

export default EditorCanvas
